import React, { useState, useEffect } from 'react';
import { Typography, Container, Box, TextField, Button, Stack, Grid, Card, CardActionArea, CardContent, CardMedia } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import InfoBanner from '../components/InfoBanner';
import PromotionCarousel from '../components/PromotionCarousel';
import HomepageBannerAd from '../components/HomepageBannerAd';
import { API_BASE_URL } from '../utils/api';

interface Promotion {
  id: string;
  title: string;
  description: string;
  imageUrl?: string;
  linkUrl?: string;
  type: 'promotion' | 'event' | 'advertisement' | 'announcement';
  startDate?: string;
  endDate?: string;
  clicks: number;
  views: number;
  isActive: boolean;
}

interface BlogPost { id: number; title: string; slug: string; excerpt?: string; coverImageUrl?: string; publishedAt?: string; }

const Dashboard: React.FC = () => {
  const [search, setSearch] = useState('');
  const [bannerMessages, setBannerMessages] = useState<string[]>([]);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [featuredPosts, setFeaturedPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  // In a real app, you'd get the user's name from context
  const userName = JSON.parse(localStorage.getItem('user') || '{}').name || 'Guest';

  const API_BASE = API_BASE_URL;
  console.log('API_BASE from environment:', process.env.REACT_APP_API_URL);
  console.log('Final API_BASE:', API_BASE);

  // Fetch banner messages and promotions from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch banner messages
        const bannerUrl = `${API_BASE}/banner-messages`;
        console.log('Banner API URL:', bannerUrl);
        const bannerResponse = await fetch(bannerUrl);
        console.log('Banner response status:', bannerResponse.status);
        const contentType = bannerResponse.headers.get('content-type');
        console.log('Banner content-type:', contentType);
        
        // Clone the response to avoid "body already used" error
        const responseClone = bannerResponse.clone();
        
        if (bannerResponse.ok && contentType && contentType.includes('application/json')) {
          try {
            const messages = await bannerResponse.json();
            setBannerMessages(messages);
          } catch (parseError) {
            // If JSON parsing fails, read as text
            const responseText = await responseClone.text();
            console.error('Error parsing banner JSON:', parseError);
            console.log('Banner response text (first 200 chars):', responseText.substring(0, 200));
            // Fallback to default messages
            setBannerMessages([
              "Welcome to NeighborhoodPulse_SA - Your trusted local services directory!",
              "Find vetted service providers in your neighborhood - Quality guaranteed!",
              "New providers joining daily - Check out our latest additions!"
            ]);
          }
        } else {
          // Read the response as text to see what we're getting
          const responseText = await bannerResponse.text();
          console.log('Banner response text (first 200 chars):', responseText.substring(0, 200));
          // Fallback to default messages
          setBannerMessages([
            "Welcome to NeighborhoodPulse_SA - Your trusted local services directory!",
            "Find vetted service providers in your neighborhood - Quality guaranteed!",
            "New providers joining daily - Check out our latest additions!"
          ]);
        }

        // Fetch promotions
        const promotionsUrl = `${API_BASE}/api/promotions`;
        console.log('Promotions API URL:', promotionsUrl);
        const promotionsResponse = await fetch(promotionsUrl);
        console.log('Promotions response status:', promotionsResponse.status);
        const promotionsContentType = promotionsResponse.headers.get('content-type');
        if (promotionsResponse.ok && promotionsContentType && promotionsContentType.includes('application/json')) {
          const promotionsData = await promotionsResponse.json();
          setPromotions(promotionsData);
        } else if (!promotionsResponse.ok) {
          const errorText = await promotionsResponse.text();
          console.error('Promotions error response:', errorText.substring(0, 200));
        }

        // Fetch featured blog posts (latest 3)
        const blogUrl = `${API_BASE}/blog`;
        console.log('Blog API URL:', blogUrl);
        const blogResponse = await fetch(blogUrl);
        console.log('Blog response status:', blogResponse.status);
        const blogContentType = blogResponse.headers.get('content-type');
        if (blogResponse.ok && blogContentType && blogContentType.includes('application/json')) {
          const blogData: BlogPost[] = await blogResponse.json();
          setFeaturedPosts(blogData.slice(0, 3));
        } else if (!blogResponse.ok) {
          const errorText = await blogResponse.text();
          console.error('Blog error response:', errorText.substring(0, 200));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        // Fallback to default messages
        setBannerMessages([
          "Welcome to NeighborhoodPulse_SA - Your trusted local services directory!",
          "Find vetted service providers in your neighborhood - Quality guaranteed!",
          "New providers joining daily - Check out our latest additions!"
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Navigate to providers page with search query (or implement search logic)
    navigate(`/providers?search=${encodeURIComponent(search)}`);
  };

  const handlePromotionClick = async (promotionId: string) => {
    try {
      // Track promotion click
      await fetch(`${API_BASE}/api/promotions/${promotionId}/click`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      console.error('Error tracking promotion click:', error);
    }
  };

  return (
    <Container>
      {!loading && (
        <InfoBanner
          messages={bannerMessages}
          speed={4000} // 4 seconds per message
        />
      )}

      {/* Homepage Banner Ad */}
      <HomepageBannerAd />

      {/* Promotions Carousel */}
      {promotions.length > 0 && (
        <Box sx={{ my: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3, color: '#2c3e50', fontWeight: 600 }}>
            Featured Promotions & Events
          </Typography>
          <PromotionCarousel 
            promotions={promotions} 
            onPromotionClick={handlePromotionClick}
          />
        </Box>
      )}

      {/* Featured Blog */}
      {featuredPosts.length > 0 && (
        <Box sx={{ my: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3, color: '#2c3e50', fontWeight: 600 }}>
            Featured Blog
          </Typography>
          <Grid container spacing={2}>
            {featuredPosts.map((p) => (
              <Grid item xs={12} sm={6} md={4} key={p.id}>
                <Card>
                  <CardActionArea onClick={() => navigate(`/blog/${p.slug}`)}>
                    {p.coverImageUrl && (
                      <CardMedia component="img" height="140" image={p.coverImageUrl} alt={p.title} />
                    )}
                    <CardContent>
                      <Typography variant="h6">{p.title}</Typography>
                      {p.excerpt && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {p.excerpt}
                        </Typography>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Welcome back, {userName}!
        </Typography>
        
        <form onSubmit={handleSearch}>
          <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
            <TextField
              fullWidth
              label="Search for services..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="e.g., plumber, electrician, cleaning..."
            />
            <Button
              type="submit"
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                minWidth: 120
              }}
            >
              Search
            </Button>
          </Stack>
        </form>

        <Typography variant="h6" gutterBottom>
          Quick Actions
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
          <Button 
            variant="outlined"
            onClick={() => navigate('/categories')}
            sx={{ borderColor: '#667eea', color: '#667eea' }}
          >
            Browse Categories
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/bookings')}
            sx={{ borderColor: '#667eea', color: '#667eea' }}
          >
            My Bookings
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/providers')}
            sx={{ borderColor: '#667eea', color: '#667eea' }}
          >
            All Providers
          </Button>
        </Stack>
      </Box>
    </Container>
  );
};

export default Dashboard; 